import React, { forwardRef, useEffect, useRef } from 'react';
import { styled, InputBase } from '@mui/material';

import Chip from 'shell/components/Chip';
import Stack from 'shell/components/Stack';
import { emailRegex } from 'utils/constants';

const RootStack = styled(Stack)(({ theme }) => {
    return {
        flex: 1,
        flexWrap: 'wrap',
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
        borderRadius: '4px',
        width: '100%',
        maxHeight: 200,
        padding: theme.spacing(2),
        background: theme.palette.ensightful.shades[0],
        cursor: 'text',
        overflowY: 'auto',
        transition: `${theme.transitions.default}, outline 0ms`,
        '&:focus-within': {
            borderColor: theme.palette.ensightful.primary[700],
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[700],
        },
    };
});

const CustomInputBase = styled(InputBase)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '7.5rem',
        height: '1.25rem',
        fontSize: `${theme.typography.p2.fontSize.large}rem`,
        fontWeight: theme.typography.p2.fontWeight.regular,
        letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
        lineHeight: `${theme.typography.getLineHeight('p2', 'large')}rem`,
        '& > input': {
            padding: 0,
            '&::placeholder': {
                opacity: 1,
                color: theme.palette.ensightful.neutral[500],
            },
        },
    };
});

export default forwardRef(({ inputValue, value, onClick, onChange, onChangeInput, ...rest }, ref) => {
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    const onMouseDown = (e) => {
        if (e.target !== inputRef.current) e.preventDefault();
        inputRef.current.focus();
    };

    const handleKeyPress = (e) => {
        if (inputValue.trim()) {
            if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                onChange([...value, inputValue.trim()]);
                onChangeInput({ target: { value: '' } });
            }
        }
    };

    useEffect(() => {
        if (!inputValue.length) {
            rootRef.current.scrollTop = rootRef.current.scrollHeight;
        }
    }, [inputValue.length]);

    return (
        <RootStack
            ref={rootRef}
            {...rest}
            alignItems={'center'}
            direction={'row'}
            spacing={1}
            onMouseDown={onMouseDown}
        >
            {value.map((email, index) => {
                const isEmailValid = emailRegex.test(email);
                return (
                    <Chip
                        key={index}
                        color={isEmailValid ? 'default' : 'error'}
                        content={email}
                        onClick={() => onClick(email, index)}
                    />
                );
            })}
            <CustomInputBase
                inputRef={inputRef}
                placeholder={!value.length ? 'Email, comma separated' : ''}
                value={inputValue}
                onChange={onChangeInput}
                onKeyPress={handleKeyPress}
            />
        </RootStack>
    );
});
